import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '../index';

interface Notification {
  id: string;
  message: string;
  type: 'info' | 'success' | 'warning' | 'error';
  duration?: number; // Optional duration in ms
}

interface SystemState {
  notifications: Notification[];
  isOnline: boolean;
  theme: 'light' | 'dark' | 'system'; // Match ThemeContext if syncing, Added 'system'
}

const initialState: SystemState = {
  notifications: [],
  isOnline: navigator.onLine, // Initial online status
  theme: 'system', // Default or read from localStorage/ThemeContext initial, Changed to 'system'
};

let notificationIdCounter = 0;

const systemSlice = createSlice({
  name: 'system',
  initialState,
  reducers: {
    addNotification: (
        state,
        action: PayloadAction<Omit<Notification, 'id'>>
    ) => {
      const id = `notification-${notificationIdCounter++}`;
      state.notifications.push({ ...action.payload, id });
    },
    removeNotification: (state, action: PayloadAction<string>) => {
      state.notifications = state.notifications.filter(
        (n) => n.id !== action.payload
      );
    },
    setIsOnline: (state, action: PayloadAction<boolean>) => {
      state.isOnline = action.payload;
    },
    setTheme: (state, action: PayloadAction<'light' | 'dark' | 'system'>) => {
      state.theme = action.payload;
      // Potentially sync with ThemeContext or localStorage here if needed
    },
  },
});

export const {
  addNotification,
  removeNotification,
  setIsOnline,
  setTheme,
} = systemSlice.actions;

// Selectors
export const selectNotifications = (state: RootState) => state.system.notifications;
export const selectIsOnline = (state: RootState) => state.system.isOnline;
export const selectTheme = (state: RootState) => state.system.theme;

export default systemSlice.reducer; 